/**
 * AppHttp bond setup
 *
 * Configures the HTTP client with the API base URL and a global error
 * interceptor that surfaces server errors and connectivity issues to the user
 * via the notification system.
 */

import { getClient as getAuthClient, hasClient as hasAuthClient } from '@molecule/app-auth'
import { createFetchClient, type HttpClient, isAborted, setClient } from '@molecule/app-http'
import { t } from '@molecule/app-i18n'
import { getProvider as getStorageProvider } from '@molecule/app-storage'

import { API_BASE_URL } from '../config.js'
import { emitNotification, type NotificationAction } from '../notifications.js'
import {
  allowanceResetLine,
  balanceEmptyLine,
  billingCta,
  type BillingCtaAction,
} from '../upgrade-copy.js'
import { hasGuestCookie } from '../utilities/guestClaim.js'
import { sandboxErrorAction, sandboxErrorSentence } from '../utilities/sandbox-error-copy.js'
import {
  AI_BUDGET_HIT_DAY_KEY,
  notifyUsageInvalidated,
  utcDayBucket,
} from '../utilities/usage-utilities.js'
import { waitPhrase } from '../utilities/wait-phrase.js'

/** The daily AI caps whose 429 carries a `resetAt` and warrants reset-first copy. */
const DAILY_AI_LIMIT_TYPES = new Set(['ai_cost', 'ai_messages'])

/**
 * The prepaid usage balance ran dry (402 `limitType: 'usage_balance'`). Carried
 * as 402 rather than 429 because nothing resets it on a timer — the user has to
 * fund the account — so it needs its own branch, and its own local fallback
 * line for the rare body that carries no message.
 */
const USAGE_BALANCE_LIMIT_TYPE = 'usage_balance'

/**
 * Read the limit body's `upgradeTier` — the next tier up, or an EXPLICIT `null`
 * when there is none (the top tier). The distinction is load-bearing: `null`
 * means "offer no upgrade", while a missing field only means an API that does
 * not state it, which must keep the ordinary Upgrade path.
 *
 * @param data - The limit response body.
 * @returns The next tier, `null` when there is none, or undefined when unstated.
 */
function readUpgradeTier(data: Record<string, unknown> | undefined): string | null | undefined {
  if (data?.upgradeTier === null) return null
  return typeof data?.upgradeTier === 'string' ? data.upgradeTier : undefined
}

/** Whether a response body came back as a Blob (a `responseType: 'blob'` request). */
function isBlobBody(data: unknown): boolean {
  return typeof Blob !== 'undefined' && data instanceof Blob
}

/**
 * The 429 toast for a Blob error body: parse the JSON the server sent and show
 * its `errorKey` copy (with its action) or its sentence; a body that is not
 * JSON falls back to the generic line.
 *
 * @param body - The error body.
 */
async function limitToastFromBlob(body: Blob): Promise<void> {
  let data: Record<string, unknown> | undefined
  try {
    data = JSON.parse(await body.text()) as Record<string, unknown>
  } catch (_error) {
    // Not JSON (an HTML gateway page): the generic line below is all we can say.
    data = undefined
  }
  const errorKey = typeof data?.errorKey === 'string' ? data.errorKey : null
  const lead =
    sandboxErrorSentence(data) || 'Too many requests. Please wait a moment and try again.'
  emitNotification(lead, 'warning', sandboxErrorAction(errorKey) ?? undefined)
}

/**
 * The toast shows ONE action, so take the first of the resolved CTA's buttons
 * (the Sign up + Log in pair leads with Sign up) and narrow it to the
 * notification shape. Rebuilt field by field rather than spread, so the banner
 * never carries CTA-table internals (`id`, `color`) that it does not render.
 *
 * @param actions - The resolved CTA buttons, or undefined when there are none.
 * @returns The notification action, or undefined.
 */

function toastAction(actions: BillingCtaAction[] | undefined): NotificationAction | undefined {
  const first = actions?.[0]
  if (!first) return undefined
  return {
    label: first.label,
    href: first.href,
    ...(first.external ? { external: true } : {}),
  }
}

/**
 * When a daily AI cap 429 was last seen in THIS session (in-memory only, ms
 * epoch; 0 = never). Read synchronously by {@link dailyAiLimitRecentlyHit} so
 * autonomous fix dispatchers can skip sends that cannot start a turn.
 */
let lastDailyAiLimitHitMs = 0

/**
 * How long after a daily-AI-cap 429 the workspace's AUTONOMOUS fix dispatches
 * (stuck preview, runtime errors, health-probe failures) stay suppressed. While
 * the cap is active a dispatch cannot start a turn — it only appends the same
 * report plus a limit error to the chat, over and over (observed live: a frozen
 * `/login` preview re-reported the identical message many times in a row while
 * the user was out of free usage). After the window one dispatch is allowed
 * through again; if the cap is still active its 429 re-arms the suppression.
 * Manual "Fix with AI" is never gated by this.
 */
export const DAILY_AI_LIMIT_SUPPRESS_MS = 30 * 60_000

/**
 * Whether a daily AI cap 429 was recorded within the suppression window — the
 * signal for autonomous fix dispatchers to stay quiet (see
 * {@link DAILY_AI_LIMIT_SUPPRESS_MS}). Session-scoped: a page refresh clears
 * it, and the first suppressed-worthy dispatch after that simply 429s once and
 * re-arms it.
 *
 * @param withinMs - Override of the suppression window (for tests).
 * @returns Whether a daily AI cap hit is recent enough to suppress autonomous sends.
 */
export function dailyAiLimitRecentlyHit(withinMs: number = DAILY_AI_LIMIT_SUPPRESS_MS): boolean {
  return lastDailyAiLimitHitMs !== 0 && Date.now() - lastDailyAiLimitHitMs < withinMs
}

/**
 * Record that the user hit a daily AI cap today (UTC day-bucket), via the
 * storage adapter. The returning-session budget-reset banner reads this to
 * welcome the user back once the budget refreshes. Best-effort: a storage
 * failure must never break the limit notification. Also stamps the in-memory
 * marker {@link dailyAiLimitRecentlyHit} reads.
 */
export function recordDailyLimitHit(): void {
  lastDailyAiLimitHitMs = Date.now()
  try {
    void getStorageProvider()
      .set(AI_BUDGET_HIT_DAY_KEY, utcDayBucket(Date.now()))
      .catch(() => {
        // Async storage write failed — the banner is purely additive, so swallow.
      })
  } catch (_error) {
    // Storage provider not wired (e.g. very early boot) — non-fatal.
  }
}

/**
 * Whether an HTTP error originated from the project-LOAD request that hydrates
 * the workspace: a `GET /projects/:id` (exactly ONE id segment — NOT the
 * `/projects` list, and NOT a `/projects/:id/<sub>` resource such as
 * `/deployments`, `/sandbox/status`, `/conversations`, `/files/...`).
 *
 * P5-11: when that request 429s, Workspace.tsx already renders a CENTERED
 * rate-limit view on the page, so the floating warning banner would be
 * redundant there. We suppress the banner for this one request only and keep
 * emitting it for every other 429 (chat, actions, etc.). App-specific routing
 * knowledge is fine in this app-specific bond.
 *
 * @param config - The failed request's config (`error.config`), or undefined.
 * @returns Whether the request was the workspace project-load GET.
 */
function isProjectLoadRequest(config?: { method?: string; url?: string }): boolean {
  if (!config || config.method !== 'GET' || typeof config.url !== 'string') return false
  // `config.url` is the original relative path (baseURL is not prepended to it).
  const path = config.url.split('?')[0]
  return /^\/?projects\/[^/]+$/.test(path)
}

/**
 * `RequestConfig.options` key a caller sets to keep a GENERIC failure off the
 * floating toast — because that caller renders the failure itself.
 *
 * The interceptor is workspace-wide and cannot know that the Logs panel already
 * paints "Failed to read logs." inside the open modal, so one exec timeout
 * produced BOTH a toast (above the modal, `z-index` 10000) and the panel's own
 * red banner, saying the same thing twice — and on a non-JSON edge 502, saying
 * two DIFFERENT things.
 *
 * It suppresses only the two GENERIC buckets (unreachable server, 5xx). Limit
 * responses (429/403) still toast: those carry an Upgrade/Billing action the
 * panel does not offer, and losing them would cost the user the way out.
 */
export const SILENT_ERROR_OPTION = 'molSilentErrorNotification'

/**
 * Request config that keeps a generic failure off the floating toast.
 *
 * Spread into a request whose caller shows its own error, e.g.
 * `http.get(path, { params, ...silentErrors() })`.
 *
 * @returns The `options` fragment to spread into a request config.
 */
export function silentErrors(): { options: Record<string, unknown> } {
  return { options: { [SILENT_ERROR_OPTION]: true } }
}

/**
 * `RequestConfig.options` key a caller sets, besides {@link SILENT_ERROR_OPTION},
 * to also keep a PLAIN rate-limit 429 (no `limitType`) off the toast because the
 * caller words it itself. A 429 carrying a `limitType` still toasts.
 */
export const SILENT_RATE_LIMIT_OPTION = 'molSilentRateLimitNotification'

/**
 * Like {@link silentErrors}, and a plain rate-limit 429 is the caller's to word too.
 *
 * @returns The `options` fragment to spread into a request config.
 */
export function silentErrorsAndRateLimit(): { options: Record<string, unknown> } {
  return { options: { [SILENT_ERROR_OPTION]: true, [SILENT_RATE_LIMIT_OPTION]: true } }
}

/**
 * Whether the failed request asked to own its own error message.
 *
 * @param config - The failed request's config (`error.config`), or undefined.
 * @returns Whether the generic toast should be suppressed.
 */
function rendersItsOwnError(config?: { options?: Record<string, unknown> }): boolean {
  return config?.options?.[SILENT_ERROR_OPTION] === true
}

/**
 * Routes whose 401 is the AUTH FLOW answering, not a session ending: a wrong
 * password on log-in, a failed refresh, the current-user probe itself, a guest
 * claim. Matched on the request path (`config.url`, relative, no baseURL).
 */
const AUTH_FLOW_PATHS = [
  /^\/?users\/log-in/,
  /^\/?users\/logout/,
  /^\/?users\/me\/?$/,
  /^\/?users\/me\/password/,
  /^\/?users\/forgot-password/,
  /^\/?users\/reset-password/,
  /^\/?users\/oauth/,
  /^\/?users\/?$/,
  /^\/?auth\//,
  /^\/?guest\//,
]

/**
 * Whether a failed request belongs to the auth flow itself (see
 * {@link AUTH_FLOW_PATHS}), so its 401 must not end the session.
 *
 * @param config - The failed request's config (`error.config`), or undefined.
 * @returns Whether the request was an auth-flow route.
 */
function isAuthFlowRequest(config?: { url?: string }): boolean {
  if (!config || typeof config.url !== 'string') return false
  const path = config.url.split('?')[0]
  return AUTH_FLOW_PATHS.some((pattern) => pattern.test(path))
}

/** localStorage key of the per-browser guest marker (outlives the guest cookies). */
export const GUEST_MARKER_KEY = 'mol.guestSession'

/** Read the `mol_guest_uid` cookie value, or null. */
function readGuestUid(): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(/(?:^|;\s*)mol_guest_uid=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : null
}

/**
 * Whether this browser has a guest marker. The guest cookies share the guest
 * row's lifetime, so at natural expiry the browser drops them before the tab
 * learns the session is gone; the marker is what remembers it was a guest.
 *
 * @returns `true` when the marker is set (false when storage is unavailable).
 */
export function hasGuestMarker(): boolean {
  try {
    return localStorage.getItem(GUEST_MARKER_KEY) !== null
  } catch (_error) {
    return false
  }
}

/** Drop the guest marker (full claim, logout, sign-in, or after the notice). */
export function clearGuestMarker(): void {
  try {
    localStorage.removeItem(GUEST_MARKER_KEY)
  } catch (_error) {
    // Storage unavailable: nothing was written, nothing to clear.
  }
}

/**
 * Record that this browser is a guest while the guest cookie is still present
 * (keeps the original `since`). A signed-in tab is never a guest, so it drops
 * any marker instead.
 */
export function syncGuestMarker(): void {
  try {
    if (hasAuthClient() && getAuthClient().isAuthenticated()) {
      clearGuestMarker()
      return
    }
    const uid = readGuestUid()
    if (!uid) return
    let since = Date.now()
    try {
      const prev = JSON.parse(localStorage.getItem(GUEST_MARKER_KEY) ?? 'null') as {
        uid?: string
        since?: number
      } | null
      if (prev?.uid === uid && typeof prev.since === 'number') since = prev.since
    } catch (_error) {
      // Unparseable marker: rewrite it.
    }
    localStorage.setItem(GUEST_MARKER_KEY, JSON.stringify({ uid, since }))
  } catch (_error) {
    // Storage unavailable: the cookie check still covers the live-cookie case.
  }
}

/** Paths where a GUEST session is in use; elsewhere a guest 401 is not ours to act on. */
const GUEST_APP_PATHS = /^\/(dashboard|workspace)(\/|$)/

/**
 * Minimum gap between two session-ended handlings. A dead session makes every
 * in-flight request 401 at once; one toast and one redirect is enough.
 */
export const SESSION_ENDED_COOLDOWN_MS = 30_000

/** When the session-ended path last ran (ms epoch; 0 = never). */
let lastSessionEndedMs = 0
/** Whether a confirm probe is in flight, so a burst of 401s probes once. */
let sessionProbeInFlight = false
/** When an EXPLICIT logout started (ms epoch; 0 = none), so its 401s never re-route. */
let explicitLogoutAtMs = 0

/** Reset the daily-AI-limit marker (tests only). */
export function resetDailyAiLimitGuard(): void {
  lastDailyAiLimitHitMs = 0
}

/** Reset the session-ended guard (tests only). */
export function resetSessionEndedGuard(): void {
  lastSessionEndedMs = 0
  sessionProbeInFlight = false
  explicitLogoutAtMs = 0
}

/**
 * Record that the user asked to sign out. Any 401 arriving while the logout is in
 * flight (a request that was already on the wire, the logout call itself) belongs
 * to an intentional sign-out: the caller performs the ONE navigation, so the
 * session-ended path must not probe, toast, or navigate again.
 */
export function markExplicitLogout(): void {
  explicitLogoutAtMs = Date.now()
}

/**
 * Client-side route change from outside React. The router bond's own
 * `navigate` falls back to a full page load here (no hook-bound navigate),
 * which would drop the toast just raised — so push the entry and fire the
 * `popstate` the BrowserRouter listens to; it re-renders at the new path.
 *
 * @param path - The in-app path to show.
 */
function navigateInApp(path: string): void {
  if (typeof window === 'undefined' || window.location.pathname === path) return
  const prev = (window.history.state ?? {}) as { idx?: unknown }
  const idx = typeof prev.idx === 'number' ? prev.idx + 1 : 0
  window.history.pushState(
    { usr: null, key: Math.random().toString(36).slice(2, 10), idx },
    '',
    path,
  )
  window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }))
}

/**
 * A request came back 401: the session behind this tab may have ended (the
 * account was deleted, a guest's session expired, the cookies were cleared).
 * Confirm with the current-user probe — some 401s are a single route refusing,
 * not a dead session — then clear the client's auth state, say so once, and
 * route the user to where they can continue:
 *
 * - a SIGNED-IN tab goes to `/login`;
 * - a GUEST tab goes to `/`, the only place a fresh guest is minted (a new
 *   prompt on the landing page); `/login` would demand an account a guest
 *   never had.
 *
 * Guarded: never for the auth-flow routes themselves, only when this tab
 * believed it had a session, one probe at a time, and at most once per
 * {@link SESSION_ENDED_COOLDOWN_MS}.
 *
 * @param client - The app's http client (its bearer is dropped on a dead session).
 * @param config - The failed request's config.
 */
async function handleUnauthorized(
  client: Pick<HttpClient, 'setAuthToken'>,
  config?: { url?: string },
): Promise<void> {
  if (isAuthFlowRequest(config)) return
  // An intentional sign-out owns its navigation. A 401 that arrives while it is
  // in flight (an already-sent request, the logout call's own answer) must not
  // add a second redirect or a "session ended" toast on top of it.
  if (explicitLogoutAtMs !== 0 && Date.now() - explicitLogoutAtMs < SESSION_ENDED_COOLDOWN_MS) {
    return
  }
  if (sessionProbeInFlight) return
  if (lastSessionEndedMs !== 0 && Date.now() - lastSessionEndedMs < SESSION_ENDED_COOLDOWN_MS) {
    return
  }
  syncGuestMarker()
  const auth = hasAuthClient() ? getAuthClient() : null
  const wasSignedIn = auth?.isAuthenticated() === true
  const wasGuest =
    !wasSignedIn &&
    (hasGuestCookie() || hasGuestMarker()) &&
    typeof window !== 'undefined' &&
    GUEST_APP_PATHS.test(window.location.pathname)
  // An anonymous visitor (public pages, a shared project) has no session to end.
  if (!wasSignedIn && !wasGuest) return

  sessionProbeInFlight = true
  try {
    // Cookie-only probe: the server prefers a bearer, and a stale WS ticket in
    // the http client must not decide whether the SESSION is alive.
    const probe = await fetch(`${API_BASE_URL}/users/me`, {
      method: 'GET',
      credentials: 'include',
    })
    if (probe.status !== 401) return
  } catch (_error) {
    // Unreachable server: that is the network branch's message, not a sign-out.
    return
  } finally {
    sessionProbeInFlight = false
  }

  lastSessionEndedMs = Date.now()
  client.setAuthToken(null)
  if (typeof document !== 'undefined') {
    // The guest hint outlives the session behind it; drop it so guest-gated
    // pages (AuthGuard allowGuest, guest CTAs) stop treating this browser as one.
    document.cookie = 'mol_guest_uid=; Max-Age=0; path=/'
  }
  // Acted on: a later anonymous visit must not be misread as this guest.
  clearGuestMarker()
  if (auth && wasSignedIn) {
    // Clears the client's user, tokens and the `mol_auth` hint; its server
    // call fails quietly on the already-dead session.
    await auth.logout().catch(() => {})
  }
  if (wasSignedIn) {
    emitNotification(
      t('auth.sessionEnded', undefined, {
        defaultValue: 'Your session ended. Sign in again to continue.',
      }),
      'warning',
    )
    navigateInApp('/login')
  } else {
    emitNotification(
      t('auth.guestSessionEnded', undefined, {
        defaultValue: 'Your guest session ended. Start a new project to continue.',
      }),
      'warning',
    )
    navigateInApp('/')
  }
}

export function setupAppHttp(): void {
  // The API may be on a separate origin. Session credentials must travel on
  // normal project/share/clone calls as they do on the auth client's own calls.
  const client = createFetchClient({ baseURL: API_BASE_URL, withCredentials: true })

  syncGuestMarker()
  client.addErrorInterceptor((error) => {
    syncGuestMarker()
    const ownsItsError = rendersItsOwnError(error.config)
    if (error.status === 0) {
      // An ABORTED request (a component unmounting, a route change cancelling
      // the fetch) is a cancellation, not an outage — stay silent. Only a real
      // network failure is worth the "check your connection" toast.
      if (!ownsItsError && !isAborted(error)) {
        emitNotification('Unable to reach the server. Check your connection.', 'error')
      }
    } else if (error.status === 401) {
      // The session may have ended underneath this tab — see handleUnauthorized.
      void handleUnauthorized(client, error.config)
    } else if (error.status === 429 && isBlobBody(error.response?.data)) {
      // A blob-typed request (the export bundle) parses even the ERROR body as
      // a Blob, hiding `errorKey`/`error` — read it back before choosing the
      // copy, instead of a generic "too many requests" that contradicts the
      // sentence the server sent.
      void limitToastFromBlob(error.response?.data as Blob)
    } else if (error.status === 429) {
      // Rate limited — tell the user to slow down
      const data = error.response?.data as Record<string, unknown> | undefined
      const retryAfter = typeof data?.retryAfter === 'number' ? data.retryAfter : undefined
      const limitType = typeof data?.limitType === 'string' ? data.limitType : undefined
      const hasLimitType = limitType != null
      const requiresSignup = data?.requiresSignup === true
      const resetAt = typeof data?.resetAt === 'number' ? data.resetAt : undefined
      const isGlobal = data?.global === true
      // CST4: for a per-user AI allowance cap, LEAD with when the allowance
      // refreshes ("in ~Xh / in about 12 days") — the retention rationale — with
      // Upgrade as the secondary action. The platform-wide capacity case
      // (`global`) keeps its own honest message; bare rate-limiting falls back
      // to the server string.
      const isDailyAiLimit = limitType != null && DAILY_AI_LIMIT_TYPES.has(limitType) && !isGlobal
      const currentTier = typeof data?.currentTier === 'string' ? data.currentTier : undefined
      // The SERVER's decision about what actually fixes this limit. The copy
      // below is unchanged; only the button comes from here now, so the toast
      // can no longer offer "View plans" to a user whose prepaid balance is
      // empty or whose own spend cap paused metering.
      const billingAction = typeof data?.billingAction === 'string' ? data.billingAction : undefined
      const upgradeTier = readUpgradeTier(data)
      // Which span the exhausted allowance covers (`ai_cost` only; absent on an
      // older API, which falls back to the daily framing). Without it the toast
      // told a paying user their MONTHLY allowance was "today's free AI budget".
      const allowanceWindow =
        data?.allowanceWindow === 'billing-period' || data?.allowanceWindow === 'daily'
          ? data.allowanceWindow
          : undefined
      const lead = isDailyAiLimit
        ? allowanceResetLine(resetAt, undefined, currentTier, allowanceWindow)
        : sandboxErrorSentence(data) ||
          (retryAfter
            ? `Rate limited. Please wait ${waitPhrase(retryAfter)}.`
            : 'Too many requests. Please wait a moment and try again.')
      if (isDailyAiLimit) recordDailyLimitHit()
      // A budget/limit 429 means usage just changed — tell any mounted nudge to
      // re-fetch now, so the banner appears without waiting for a page refresh (G2).
      if (hasLimitType) notifyUsageInvalidated()
      // One resolver for every limit: the server's `billingAction` decides, and
      // only when it says nothing does the limitType/guest derivation run. A
      // bare rate-limit (no limitType) still carries no action — there is
      // nothing to buy, the user just waits.
      const action = hasLimitType
        ? toastAction(billingCta({ billingAction, limitType, requiresSignup, upgradeTier }).actions)
        : (sandboxErrorAction(typeof data?.errorKey === 'string' ? data.errorKey : null) ??
          undefined)
      // P5-10b: the TRANSIENT banner stays ONE short, scannable line — just the lead
      // (+ the Upgrade/View-plans action). The longer honest "free + open source,
      // limits exist" note (`honestLimitNote()`) no longer rides along on the
      // fleeting toast; it still lands on the in-chat upgrade card and the pricing
      // page, which is where a user can actually read it.
      // P5-11: suppress the banner for a project-LOAD 429 — Workspace.tsx already
      // shows a centered rate-limit view, so the floating toast would be redundant
      // there. Every other 429 (chat, actions) still surfaces it.
      // A caller that opted in (`silentErrorsAndRateLimit()`) words a plain rate
      // limit itself; a limit with a `limitType` still toasts for its action.
      const ownsRateLimit =
        error.config?.options?.[SILENT_RATE_LIMIT_OPTION] === true && !hasLimitType
      if (!isProjectLoadRequest(error.config) && !ownsRateLimit) {
        emitNotification(lead, 'warning', action)
      }
    } else if (error.status === 402) {
      // Payment required — metered usage could not be funded. One plain sentence
      // + the one action that fixes it, which is NOT always "Add funds": with no
      // card on file it is "Add a payment method", past the per-period top-up
      // velocity rail it is "Contact support", and for a teammate on someone
      // else's project there is no button at all. The server says which.
      const data = error.response?.data as Record<string, unknown> | undefined
      if (typeof data?.limitType === 'string') {
        notifyUsageInvalidated()
        if (!isProjectLoadRequest(error.config)) {
          const cta = billingCta({
            billingAction: typeof data.billingAction === 'string' ? data.billingAction : undefined,
            limitType: data.limitType,
            requiresSignup: data.requiresSignup === true,
            upgradeTier: readUpgradeTier(data),
          })
          const fallback =
            data.limitType === USAGE_BALANCE_LIMIT_TYPE
              ? balanceEmptyLine()
              : 'Payment required to continue.'
          const message = (typeof data.error === 'string' && data.error) || fallback
          emitNotification(
            // `owner_only` has no button, so the note IS the way out — append it
            // rather than leaving the teammate a message with nothing to do.
            cta.note ? `${message} ${cta.note}` : message,
            'warning',
            toastAction(cta.actions),
          )
        }
      } else if (!ownsItsError) {
        emitNotification(
          (typeof data?.error === 'string' && data.error) || 'Payment required.',
          'error',
        )
      }
    } else if (error.status === 403) {
      // Limit errors (project/deployment limits) — show upgrade CTA
      const data = error.response?.data as Record<string, unknown> | undefined
      if (typeof data?.limitType === 'string') {
        const message = (typeof data.error === 'string' && data.error) || 'Limit reached.'
        // P5-10b: keep this transient banner to the short limit line + action; the
        // longer honest note lives on the in-chat card / pricing page, not the toast.
        // The action comes from the same resolver as every other limit surface, so
        // a spend-cap pause routes to Billing and a top-tier account gets no
        // button instead of a plans modal with nothing to sell it.
        const cta = billingCta({
          billingAction: typeof data.billingAction === 'string' ? data.billingAction : undefined,
          limitType: data.limitType,
          requiresSignup: data.requiresSignup === true,
          upgradeTier: readUpgradeTier(data),
        })
        emitNotification(
          cta.note ? `${message} ${cta.note}` : message,
          'warning',
          toastAction(cta.actions),
        )
        notifyUsageInvalidated()
      }
    } else if (error.status >= 500) {
      // Server error — surface it so the user knows something went wrong, unless
      // the caller already shows it where the user is looking.
      if (!ownsItsError) {
        const data = error.response?.data as Record<string, unknown> | undefined
        const message =
          (typeof data?.error === 'string' && data.error) || 'Something went wrong on our end.'
        emitNotification(message, 'error')
      }
    }
    // Always return the error so callers can handle it locally too
    return error
  })

  setClient(client)
}
